import { useState } from "react";
import {
  ShieldCheck, ShieldX, Phone, Mail, CheckCircle2,
  AlertCircle, AlertTriangle, Loader2, Globe, Calendar, Server, User, MapPin, Radio,
  Sparkles, UserCheck, Lightbulb, Trash2, AtSign, Brain, BadgeCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { API, apiCall, COUNTRIES } from "@/lib/api";

const RdapSection = ({ rdap }) => {
  if (!rdap) return null;
  return (
    <div className="mt-4 pt-4 border-t border-white/10" data-testid="rdap-section">
      <div className="flex items-center gap-2 mb-3">
        <Globe className="w-4 h-4 text-cyan-400" />
        <span className="text-xs font-semibold tracking-widest uppercase text-cyan-400">Datos RDAP (NIC.ar)</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className="text-xs text-zinc-500 block mb-0.5">Dominio</span>
          <span className="text-sm text-white font-mono" data-testid="rdap-domain">{rdap.domain}</span>
        </div>
        <div>
          <span className="text-xs text-zinc-500 block mb-0.5">Estado</span>
          <Badge className={rdap.status === "active" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-rose-500/10 text-rose-400 border-rose-500/20"} data-testid="rdap-status">
            {rdap.status === "active" ? "Activo" : "Inactivo"}
          </Badge>
        </div>
        {rdap.registrant && (
          <div className="col-span-2">
            <span className="text-xs text-zinc-500 block mb-0.5">Registrante</span>
            <span className="text-sm text-white flex items-center gap-1.5" data-testid="rdap-registrant">
              <User className="w-3 h-3 text-zinc-400" />{rdap.registrant}
            </span>
          </div>
        )}
        {rdap.registration_date && (
          <div>
            <span className="text-xs text-zinc-500 block mb-0.5">Registro</span>
            <span className="text-sm text-zinc-300 flex items-center gap-1.5" data-testid="rdap-reg-date">
              <Calendar className="w-3 h-3 text-zinc-500" />{rdap.registration_date}
            </span>
          </div>
        )}
        {rdap.expiration_date && (
          <div>
            <span className="text-xs text-zinc-500 block mb-0.5">Vencimiento</span>
            <span className={`text-sm flex items-center gap-1.5 ${rdap.expired ? "text-rose-400" : "text-zinc-300"}`} data-testid="rdap-exp-date">
              <Calendar className="w-3 h-3 text-zinc-500" />{rdap.expiration_date}
              {rdap.expired && <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20 text-[10px] ml-1">Expirado</Badge>}
            </span>
          </div>
        )}
        {rdap.nameservers?.length > 0 && (
          <div className="col-span-2">
            <span className="text-xs text-zinc-500 block mb-0.5">Nameservers</span>
            <div className="flex flex-wrap gap-1.5" data-testid="rdap-nameservers">
              {rdap.nameservers.map((ns, i) => (
                <Badge key={i} className="bg-zinc-800 text-zinc-400 border-zinc-700 text-xs font-mono">
                  <Server className="w-2.5 h-2.5 mr-1" />{ns}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AiScoringSection = ({ ai }) => {
  if (!ai || ai.error) return null;
  const score = ai.score_riesgo ?? 0;
  const color = score <= 30 ? "emerald" : score <= 60 ? "amber" : "rose";
  return (
    <div className="mt-4 pt-4 border-t border-white/10" data-testid="ai-scoring-section">
      <div className="flex items-center gap-2 mb-3">
        <Brain className="w-4 h-4 text-violet-400" />
        <span className="text-xs font-semibold tracking-widest uppercase text-violet-400">Analisis IA (Gemini)</span>
      </div>
      <div className="flex items-center gap-4 mb-3">
        <div className={`w-14 h-14 rounded-lg flex flex-col items-center justify-center bg-${color}-500/10 border border-${color}-500/20`}>
          <span className={`text-lg font-bold text-${color}-400`}>{score}</span>
          <span className="text-[9px] text-zinc-500">riesgo</span>
        </div>
        <div className="flex-1 space-y-1">
          <p className="text-sm text-zinc-300">{ai.razon}</p>
          <div className="flex flex-wrap gap-1.5">
            {ai.es_aleatorio && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px]">String Aleatorio</Badge>}
            {ai.es_bot && <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20 text-[10px]">Posible Bot</Badge>}
            {ai.patron_sospechoso && <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20 text-[10px]">Patron Sospechoso</Badge>}
            {!ai.es_aleatorio && !ai.es_bot && !ai.patron_sospechoso && <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]">Sin alertas</Badge>}
            {ai.nombre_coincide === true && <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]">Nombre coincide</Badge>}
            {ai.nombre_coincide === false && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px]">Nombre no coincide</Badge>}
          </div>
        </div>
      </div>
    </div>
  );
};

const NameSimilaritySection = ({ sim }) => {
  if (!sim) return null;
  const score = sim.score || 0;
  // Buckets: high >=70, medium 40-69, low <40
  const tier = score >= 70 ? "high" : score >= 40 ? "medium" : "low";
  const tierMap = {
    high: {
      ring: "border-emerald-500/40",
      bg: "bg-emerald-500/10",
      text: "text-emerald-600 dark:text-emerald-400",
      icon: "text-emerald-500",
      title: "Verificado por Nombre",
      subtitle: "El email coincide con el nombre del titular",
    },
    medium: {
      ring: "border-amber-500/40",
      bg: "bg-amber-500/10",
      text: "text-amber-600 dark:text-amber-400",
      icon: "text-amber-500",
      title: "Coincidencia Parcial",
      subtitle: "Algunos elementos del nombre estan presentes",
    },
    low: {
      ring: "border-rose-500/40",
      bg: "bg-rose-500/10",
      text: "text-rose-600 dark:text-rose-400",
      icon: "text-rose-500",
      title: "Sin Coincidencia",
      subtitle: "El email no matchea con el nombre indicado",
    },
  };
  const t = tierMap[tier];
  return (
    <div className={`col-span-2 mt-2 p-4 rounded-lg border-2 ${t.ring} ${t.bg} flex items-start gap-3`} data-testid="name-similarity-section">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-white/5 flex-shrink-0`}>
        <BadgeCheck className={`w-6 h-6 ${t.icon}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-bold text-sm ${t.text}`} data-testid="name-verified-title">{t.title}</span>
          <Badge className={`${t.bg} ${t.text} border-current text-[10px] font-bold`} data-testid="name-similarity-score">
            {score}%
          </Badge>
          {sim.initials_match && (
            <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20 text-[10px]">Iniciales</Badge>
          )}
        </div>
        <p className="text-xs mt-0.5" style={{ color: "var(--app-text-muted)" }}>
          {t.subtitle}
        </p>
        {sim.analysis && (
          <p className="text-xs mt-1" style={{ color: "var(--app-text-secondary)" }}>
            <span className="opacity-70">Analisis:</span> {sim.analysis}
          </p>
        )}
        {/* Progress bar */}
        <div className="mt-2 w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div
            className={`h-full transition-all ${tier === "high" ? "bg-emerald-500" : tier === "medium" ? "bg-amber-500" : "bg-rose-500"}`}
            style={{ width: `${Math.min(100, score)}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const EmailResultCard = ({ result }) => {
  if (!result) return null;
  const isValid = result.is_valid;
  const hasSuggestions = result.autocomplete_suggestions?.length > 0;
  const Icon = isValid ? ShieldCheck : ShieldX;

  return (
    <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 result-card backdrop-blur-sm">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center ${isValid ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-white">Email</h3>
            <p className="font-mono text-sm text-zinc-400">{result.input}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {result.name_similarity?.score >= 70 && (
            <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 gap-1" data-testid="verified-by-name-badge">
              <BadgeCheck className="w-3 h-3" />Verificado por Nombre
            </Badge>
          )}
          <Badge className={isValid ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : "bg-rose-500/10 text-rose-500 border-rose-500/20"}>
            {isValid ? "Valido" : "Invalido"}
          </Badge>
        </div>
      </div>

      <div className="space-y-3">
        {/* Suggested correction */}
        {result.suggested_correction && (
          <div className="flex items-center gap-2 p-2 bg-amber-500/5 border border-amber-500/15 rounded-md" data-testid="typo-suggestion">
            <Lightbulb className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <span className="text-sm text-amber-300">Quizas quisiste decir: <strong className="font-mono">{result.suggested_correction}</strong></span>
          </div>
        )}

        {/* Autocomplete suggestions */}
        {hasSuggestions && (
          <div className="p-3 bg-cyan-500/5 border border-cyan-500/15 rounded-md" data-testid="autocomplete-suggestions">
            <div className="flex items-center gap-2 mb-2">
              <AtSign className="w-4 h-4 text-cyan-400" />
              <span className="text-xs font-semibold text-cyan-400">Email incompleto - Sugerencias:</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {result.autocomplete_suggestions.map((s, i) => (
                <Badge key={i} className="bg-cyan-500/10 text-cyan-300 border-cyan-500/20 text-xs font-mono cursor-pointer hover:bg-cyan-500/20 transition-colors">
                  {s}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Core checks grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2" data-testid="domain-check">
            {result.domain_exists ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertCircle className="w-4 h-4 text-rose-400" />}
            <span className="text-sm text-zinc-300">Dominio existe</span>
          </div>
          <div className="flex items-center gap-2" data-testid="mx-check">
            {result.mx_records ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertCircle className="w-4 h-4 text-rose-400" />}
            <span className="text-sm text-zinc-300">Registros MX</span>
          </div>
          <div className="flex items-center gap-2" data-testid="disposable-check">
            {result.is_disposable ? <Trash2 className="w-4 h-4 text-rose-400" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
            <span className="text-sm text-zinc-300">{result.is_disposable ? "Email temporal/disposable" : "No es disposable"}</span>
          </div>
          <div className="flex items-center gap-2" data-testid="role-check">
            {result.is_role_account ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
            <span className="text-sm text-zinc-300">{result.is_role_account ? "Cuenta de rol" : "Cuenta personal"}</span>
          </div>

          {/* SMTP */}
          <div className="flex items-center gap-2 col-span-2" data-testid="smtp-verification-result">
            {result.smtp_verified === "valid" ? (
              <><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-sm text-emerald-400">SMTP: Buzon verificado</span></>
            ) : result.smtp_verified === "invalid" ? (
              <><AlertCircle className="w-4 h-4 text-rose-400" /><span className="text-sm text-rose-400">SMTP: Buzon no existe</span></>
            ) : result.smtp_verified === "catch_all" ? (
              <><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-sm text-amber-400">SMTP: Servidor catch-all (no verificable)</span></>
            ) : result.smtp_verified === "error" ? (
              <><AlertTriangle className="w-4 h-4 text-zinc-500" /><span className="text-sm text-zinc-500">SMTP: No se pudo verificar</span></>
            ) : (
              <><AlertTriangle className="w-4 h-4 text-zinc-600" /><span className="text-sm text-zinc-600">SMTP: Omitido</span></>
            )}
            {result.gmass_data && (
              <span
                data-testid="gmass-badge"
                className="ml-2 inline-flex items-center gap-1 rounded-full bg-violet-500/10 text-violet-500 border border-violet-500/30 px-2 py-0.5 text-[10px] font-medium"
                title={`GMass Status: ${result.gmass_data.Status || "-"}, SMTP Code: ${result.gmass_data.SMTPCode || result.gmass_data.StatusCode || "-"}`}
              >
                <ShieldCheck className="w-3 h-3" />
                Verificado por GMass
              </span>
            )}
          </div>

          {/* Name similarity */}
          <NameSimilaritySection sim={result.name_similarity} />
        </div>

        {/* RDAP for .ar */}
        {result.rdap_data && <RdapSection rdap={result.rdap_data} />}

        {/* AI Scoring */}
        <AiScoringSection ai={result.ai_scoring} />
      </div>
    </div>
  );
};

const PhoneResultCard = ({ result }) => {
  if (!result) return null;
  const isValid = result.is_valid;
  const Icon = isValid ? ShieldCheck : ShieldX;
  return (
    <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 result-card backdrop-blur-sm">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center ${isValid ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-white">Telefono</h3>
            <p className="font-mono text-sm text-zinc-400">{result.input}</p>
          </div>
        </div>
        <Badge className={isValid ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : "bg-rose-500/10 text-rose-500 border-rose-500/20"}>
          {isValid ? "Valido" : "Invalido"}
        </Badge>
      </div>
      <div className="space-y-4">
        {result.normalized && (
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase text-zinc-500 block mb-1">Normalizado</span>
            <span className="font-mono text-emerald-400">{result.normalized}</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase text-zinc-500 block mb-1">Pais</span>
            <span className="text-white">{result.country || "N/A"}</span>
          </div>
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase text-zinc-500 block mb-1">Tipo</span>
            <span className="text-white capitalize">{result.type === "mobile" ? "Celular" : result.type === "landline" ? "Fijo" : result.type || "N/A"}</span>
          </div>
          {result.provincia && (
            <div>
              <span className="text-xs font-semibold tracking-widest uppercase text-zinc-500 block mb-1">Provincia</span>
              <span className="text-white flex items-center gap-1.5" data-testid="phone-provincia">
                <MapPin className="w-3 h-3 text-violet-400" />{result.provincia}
              </span>
            </div>
          )}
          {result.localidad && (
            <div>
              <span className="text-xs font-semibold tracking-widest uppercase text-zinc-500 block mb-1">Localidad</span>
              <span className="text-white flex items-center gap-1.5" data-testid="phone-localidad">
                <MapPin className="w-3 h-3 text-cyan-400" />{result.localidad}
              </span>
            </div>
          )}
          {result.operador && (
            <div className="col-span-2">
              <span className="text-xs font-semibold tracking-widest uppercase text-zinc-500 block mb-1">Operador</span>
              <span className="text-white flex items-center gap-1.5" data-testid="phone-operador">
                <Radio className="w-3 h-3 text-amber-400" />{result.operador}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export const SingleTab = ({ services }) => {
  const phoneEnabled = services?.phone_validation !== false;
  const emailEnabled = services?.email_validation !== false;
  const waEnabled = services?.whatsapp_validation?.enabled || false;

  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [countryCode, setCountryCode] = useState("AR");
  const [loading, setLoading] = useState(false);
  const [phoneResult, setPhoneResult] = useState(null);
  const [emailResult, setEmailResult] = useState(null);
  const [checkWhatsApp, setCheckWhatsApp] = useState(false);
  const [waResult, setWaResult] = useState(null);
  const [waLoading, setWaLoading] = useState(false);

  const handleVerify = async () => {
    if (!phone && !email) {
      toast.error("Ingresa al menos un telefono o email");
      return;
    }
    setLoading(true);
    setPhoneResult(null);
    setEmailResult(null);
    setWaResult(null);
    try {
      const { ok, data } = await apiCall("POST", `${API}/verify`, {
        phone: phone || null,
        email: email || null,
        name: name || null,
        country_code: countryCode,
      });
      if (!ok) throw new Error("Verification failed");
      setPhoneResult(data.phone);
      setEmailResult(data.email);
      toast.success("Verificacion completada");

      if (checkWhatsApp && phone) {
        setWaLoading(true);
        try {
          const { ok: waOk, data: waData } = await apiCall("POST", `${API}/verify/whatsapp-check`, { phone, country_code: countryCode, get_picture: true });
          if (waOk) {
            if (waData.error) {
              toast.error(waData.error);
            } else {
              setWaResult(waData);
            }
          }
        } catch { /* wa is optional, ignore */ }
        finally { setWaLoading(false); }
      }
    } catch {
      toast.error("Error al verificar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm">
        <h2 className="text-xl font-bold text-white mb-6">Verificacion Individual</h2>

        {/* Name field (optional, for email analysis) */}
        {emailEnabled && (
          <div className="mb-5">
            <Label className="text-sm font-semibold text-zinc-300 flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-violet-400" />Nombre del contacto <span className="text-xs text-zinc-600 font-normal">(opcional, mejora el analisis de email)</span>
            </Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Juan Perez" className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-500 max-w-md" data-testid="name-input" />
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {phoneEnabled && (
            <div className="space-y-4">
              <Label className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
                <Phone className="w-4 h-4 text-violet-400" />Telefono
              </Label>
              <div className="flex gap-2">
                <Select value={countryCode} onValueChange={setCountryCode}>
                  <SelectTrigger className="w-32 bg-zinc-950 border-zinc-800 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {COUNTRIES.map(c => <SelectItem key={c.code} value={c.code} className="text-white hover:bg-zinc-800">{c.flag} {c.code}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="3815551234" className="flex-1 bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-500" data-testid="phone-input" />
              </div>
            </div>
          )}
          {emailEnabled && (
            <div className="space-y-4">
              <Label className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
                <Mail className="w-4 h-4 text-violet-400" />Email
              </Label>
              <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="usuario@gmail.com" className="bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-500" data-testid="email-input" />
            </div>
          )}
        </div>

        {phoneEnabled && waEnabled && (
          <div className="flex items-center justify-between mt-4 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                <Phone className="w-3 h-3 text-white" />
              </div>
              <span className="text-sm text-zinc-300">Verificar WhatsApp (UltraMsg)</span>
              <span className="text-xs text-zinc-500">Consulta si el numero tiene WhatsApp y obtiene la foto de perfil</span>
            </div>
            <Switch checked={checkWhatsApp} onCheckedChange={setCheckWhatsApp} data-testid="whatsapp-toggle" />
          </div>
        )}

        <Button onClick={handleVerify} disabled={loading || (!phone && !email)} className="mt-4 bg-violet-600 hover:bg-violet-700 text-white border border-violet-500/50" data-testid="verify-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verificando...</> : <><ShieldCheck className="w-4 h-4 mr-2" />Verificar</>}
        </Button>
      </div>

      {(phoneResult || emailResult || waResult || waLoading) && (
        <div className="grid md:grid-cols-2 gap-6">
          {phoneResult && <PhoneResultCard result={phoneResult} />}
          {emailResult && <EmailResultCard result={emailResult} />}
          {(waResult || waLoading) && (
            <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-6 backdrop-blur-sm" data-testid="whatsapp-result">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white">WhatsApp</h3>
              </div>
              {waLoading ? (
                <div className="flex items-center gap-2 text-zinc-400"><Loader2 className="w-4 h-4 animate-spin" />Consultando WhatsApp...</div>
              ) : waResult ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    {waResult.profile_picture ? (
                      <img src={waResult.profile_picture} alt="WA" className="w-16 h-16 rounded-full border-2 border-green-500/30 object-cover" data-testid="wa-profile-pic" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center">
                        <User className="w-8 h-8 text-zinc-600" />
                      </div>
                    )}
                    <div>
                      <Badge className={waResult.has_whatsapp ? "bg-green-500/10 text-green-400 border-green-500/20" : "bg-rose-500/10 text-rose-400 border-rose-500/20"} data-testid="wa-status-badge">
                        {waResult.has_whatsapp ? "Tiene WhatsApp" : "No tiene WhatsApp"}
                      </Badge>
                      {waResult.name && <p className="text-white mt-1 font-medium" data-testid="wa-name">{waResult.name}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <span>Numero consultado: {waResult.e164 || waResult.phone}</span>
                    {waResult.daily_used && waResult.daily_limit && (
                      <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-[10px]">
                        {waResult.daily_used}/{waResult.daily_limit} hoy
                      </Badge>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
